import React from "react";
import { Link } from "react-router-dom";
import { CaretRight, EnvelopeSimple, FileText, ArrowRight } from "@phosphor-icons/react";
import SectionHeading from "../components/SectionHeading";
import SEO from "../components/SEO";
import GPReferralForm from "../components/GPReferralForm";

const ROUTES = [
  {
    key: "self",
    title: "Book yourself in",
    eyebrow: "Most patients",
    desc: "You don’t need a GP letter. Most of our patients book themselves in directly.",
    cta: { to: "/enquiry", label: "Book an appointment" },
  },
  {
    key: "gp",
    title: "Ask your GP to refer you",
    eyebrow: "GP referral",
    desc: "If you’d rather, your GP can email us a short referral letter. We also accept NHS e-RS referrals via the CUH private office.",
    cta: { to: "#gp", label: "GP referral details" },
  },
];

export default function Referrals() {
  return (
    <div data-testid="referrals-page">
      <SEO
        title="Referrals — Self-referral & GP"
        description="You don’t need a GP letter to be seen at Cambridge Cardiovascular Prevention Clinic. Most patients refer themselves — or you can ask your GP."
        path="/referrals"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Do I need a GP referral?", acceptedAnswer: { "@type": "Answer", text: "No — Cambridge Cardiovascular Prevention Clinic accepts self-referrals. Most of our patients arrange their own appointment directly." } },
            { "@type": "Question", name: "How do I get an appointment?", acceptedAnswer: { "@type": "Answer", text: "Get in touch and we will offer you the earliest available appointment." } },
          ],
        }}
      />

      <section className="bg-cream-100 border-b border-bronze-light/30">
        <div className="container-ccc py-20 max-w-4xl">
          <div className="eyebrow">Three ways to be seen</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.02]">
            <em className="font-serif text-bronze-dark">You</em> don’t need a referral letter.
          </h1>
          <p className="mt-6 text-ink-muted text-lg leading-relaxed max-w-2xl">
            Most of our patients book themselves in — directly and without paperwork. If you’d rather your GP arranged it, that’s fine too.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link data-testid="referrals-self-cta" to="/enquiry" className="btn-primary inline-flex items-center gap-2">Book yourself in <ArrowRight size={16} weight="bold" /></Link>
            <a href="#gp" data-testid="referrals-gp-anchor" className="btn-secondary">Info for GPs</a>
          </div>
        </div>
      </section>

      <section className="container-ccc py-20">
        <SectionHeading eyebrow="Ways in" title="Pick what works for you." />
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {ROUTES.map((r) => (
            <div key={r.key} data-testid={`referral-route-${r.key}`} className="p-8 bg-white border border-stone-200/60 rounded-md hover:-translate-y-1 hover:shadow-md transition-all flex flex-col">
              <div className="eyebrow">{r.eyebrow}</div>
              <h3 className="font-serif text-2xl mt-3">{r.title}</h3>
              <p className="mt-3 text-ink-muted text-sm leading-relaxed flex-1">{r.desc}</p>
              {r.cta.to.startsWith("#") ? (
                <a href={r.cta.to} className="mt-6 text-bronze-dark text-sm inline-flex items-center gap-1">{r.cta.label} <CaretRight size={14} /></a>
              ) : (
                <Link to={r.cta.to} className="mt-6 text-bronze-dark text-sm inline-flex items-center gap-1">{r.cta.label} <CaretRight size={14} /></Link>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="bg-ink text-cream-50">
        <div className="container-ccc py-20">
          <div className="eyebrow text-bronze-light">Booking yourself in</div>
          <h2 className="font-serif text-3xl lg:text-4xl mt-4 max-w-3xl leading-tight">
            Here’s what happens.
          </h2>
          <div className="mt-12 grid md:grid-cols-4 gap-5">
            {[
              { n: "01", t: "Get in touch", d: "Fill in our short form — under 3 minutes." },
              { n: "02", t: "We get back to you", d: "With appointment options." },
              { n: "03", t: "Confirm your slot", d: "Pick a time that suits you and we’ll handle the rest." },
              { n: "04", t: "Come in", d: "See your consultant at a time that suits you." },
            ].map((s) => (
              <div key={s.n} className="p-6 border border-cream-100/15 rounded-md">
                <div className="font-serif text-bronze-light text-2xl">{s.n}</div>
                <div className="font-serif text-xl mt-2">{s.t}</div>
                <p className="text-cream-100/70 mt-2 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Link data-testid="referrals-self-referral-cta" to="/enquiry" className="btn-primary">Book yourself in</Link>
          </div>
        </div>
      </section>

      <section id="gp" className="bg-cream-100 border-t border-bronze-light/30">
        <div className="container-ccc py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">For GPs & referrers</div>
            <h2 className="font-serif text-3xl lg:text-4xl mt-3 leading-tight">A clear, responsive specialist partner.</h2>
            <p className="mt-5 text-ink-muted leading-relaxed">
              We see patients with resistant or young-onset hypertension, complex lipid disorders, and early cardiometabolic risk.
            </p>
            <div className="eyebrow mt-10">Patients likely to benefit most</div>
            <ul className="mt-4 space-y-3 text-ink-muted">
              {[
                "Adults under 40 with newly diagnosed hypertension",
                "Blood pressure uncontrolled on ≥3 agents (including a diuretic)",
                "Suspected secondary causes of hypertension",
                "Familial hypercholesterolaemia or statin intolerance",
                "Complex polypharmacy requiring cardiovascular medication review",
                "Elevated cardiometabolic risk requiring specialist stratification",
              ].map((x) => (
                <li key={x} className="flex gap-3 items-start"><span className="text-bronze-dark mt-1">•</span>{x}</li>
              ))}
            </ul>

            <div className="mt-10 p-6 bg-white border border-bronze-light/40 rounded-md">
              <div className="eyebrow">Other ways to refer</div>
              <ul className="mt-4 space-y-4 text-sm text-ink">
                <li className="flex gap-3 items-start"><EnvelopeSimple size={20} className="text-bronze-dark mt-0.5" weight="duotone" /><div>Email a referral letter to<br /><span className="font-medium">enquiries@camcpc.co.uk</span></div></li>
                <li className="flex gap-3 items-start"><FileText size={20} className="text-bronze-dark mt-0.5" weight="duotone" /><div>NHS e-RS via CUH private office — contact us for the referrer slot code.</div></li>
              </ul>
              <a
                href="/gp/CCC-Referral-Template.txt"
                download
                data-testid="gp-download-template"
                className="btn-secondary w-full mt-6 inline-flex items-center justify-center gap-2"
              >
                <FileText size={16} /> Download referral letter template
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="p-7 lg:p-9 bg-white border border-bronze-light/40 rounded-md">
              <div className="eyebrow">Send a referral now</div>
              <h3 className="font-serif text-2xl lg:text-3xl mt-2 leading-tight">A 60-second secure referral form.</h3>
              <p className="mt-3 text-sm text-ink-muted">Submits straight to our clinical team. The consultant will write back to you after the appointment.</p>
              <div className="mt-8">
                <GPReferralForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
